// タグの parse / serialize / 集計 / 絞り込みユーティリティ
import { TAG_SEPARATOR } from "./constants";
import type { BusinessCard } from "./types";

/** "重要, 取引先, 営業" -> ["重要","取引先","営業"](trim+空除去+重複除去) */
export function parseTags(raw: string | undefined): string[] {
  if (!raw) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(TAG_SEPARATOR)) {
    const t = part.trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

/** ["重要","取引先"] -> "重要, 取引先" */
export function serializeTags(tags: string[]): string {
  return tags.map((t) => t.trim()).filter(Boolean).join(`${TAG_SEPARATOR} `);
}

/** 全カードからユニークなタグ一覧を頻度順で返す */
export function extractAllTags(cards: BusinessCard[]): Array<{ tag: string; count: number }> {
  const counts = new Map<string, number>();
  for (const c of cards) {
    for (const t of c.tags ?? []) {
      counts.set(t, (counts.get(t) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag, "ja"));
}

/**
 * 選択中タグで絞り込み(AND条件 = 全タグを含むカードのみ)。
 * 空配列なら全件返す。
 */
export function filterByTags(
  cards: BusinessCard[],
  selectedTags: string[],
): BusinessCard[] {
  if (selectedTags.length === 0) return cards;
  return cards.filter((c) => {
    const set = new Set(c.tags ?? []);
    return selectedTags.every((t) => set.has(t));
  });
}

/** タグの追加・削除・置換ヘルパー */
export function addTag(current: string[], tag: string): string[] {
  const t = tag.trim();
  if (!t || current.includes(t)) return current;
  return [...current, t];
}

export function removeTag(current: string[], tag: string): string[] {
  return current.filter((x) => x !== tag);
}

/** タグ表示時のカラー(ハッシュベース・WCAG AA を意識した彩度) */
export function tagColorClass(tag: string): string {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) {
    hash = (hash * 31 + tag.charCodeAt(i)) | 0;
  }
  const palette = [
    "bg-skyPale text-navy",
    "bg-washiDeep text-navySoft",
    "bg-amber-100 text-amber-800",
    "bg-emerald-100 text-emerald-800",
    "bg-rose-100 text-rose-800",
    "bg-violet-100 text-violet-800",
    "bg-cyan-100 text-cyan-800",
    "bg-orange-100 text-orange-800",
  ];
  return palette[Math.abs(hash) % palette.length];
}
